import React, { useEffect, useRef, useState } from 'react';
import { Bell, BellOff, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { isPushSupported, isSubscribed, subscribeToPush, unsubscribeFromPush } from '../../utils/pushNotifications';

const BLOCKED = 'Notifications are blocked for this site. Allow them in your browser or phone settings, then tap again.';

const turnOn = async () => {
  // Ask for permission first, straight from the tap (iPhone requires this).
  const perm = Notification.permission === 'granted' ? 'granted' : await Notification.requestPermission();
  if (perm !== 'granted') throw new Error(BLOCKED);
  await subscribeToPush();
};

export const AlertsToggle = () => {
  const [on, setOn] = useState(null);
  const [busy, setBusy] = useState(false);
  const lock = useRef(false);
  const supported = isPushSupported();

  useEffect(() => {
    if (!supported) return;
    isSubscribed().then(setOn).catch(() => setOn(false));
  }, [supported]);

  if (!supported) return null;

  const toggle = async () => {
    if (lock.current) return;
    lock.current = true;
    setBusy(true);
    const wantOn = !on;
    try {
      if (wantOn) await turnOn(); else await unsubscribeFromPush();
      setOn(wantOn);
      localStorage.setItem('pushNotificationsEnabled', String(wantOn));
      toast.success(wantOn ? 'Alerts on — new trade setups will reach this device.' : 'Alerts off for this device.');
    } catch (e) {
      if (!wantOn) setOn(false);
      (wantOn ? toast.error : toast.warning)(e?.message || 'Could not update alerts — please try again.', { duration: 8000 });
      if (wantOn) setOn(await isSubscribed().catch(() => false));
    } finally {
      lock.current = false;
      setBusy(false);
    }
  };

  return (
    <button type="button" onClick={toggle} disabled={busy || on === null} data-testid="push-notification-toggle"
      className={`sim-alerts ${on ? 'sim-alerts-on' : ''}`} aria-pressed={!!on}>
      {busy || on === null ? <Loader2 size={15} className="animate-spin" /> : on ? <Bell size={15} /> : <BellOff size={15} />}
      {on ? 'Alerts On' : 'Alerts Off'}
    </button>
  );
};
